import { isFormula } from '../utils/dataUtils/formula/tableFormulaPredicateUtils';

export type FormulaPickerSession = {
    queryStartOffset: number;
    queryEndOffset: number;
};

export const createFormulaPickerSession = (
    cellContent: string,
    queryStartOffset: number,
): FormulaPickerSession | null =>
    isFormula(cellContent) ? { queryStartOffset, queryEndOffset: queryStartOffset } : null;

/*
 * Search backward from the cursor position to find the start of the query for a formula suggestion.
 */
const FORMULA_QUERY_CHARACTER_PATTERN = /[A-Za-z0-9_.]/;
const findQueryStartOffset = (cellContent: string, cursorOffset: number): number => {
    let queryStartOffset = cursorOffset;

    while (queryStartOffset > 1 && FORMULA_QUERY_CHARACTER_PATTERN.test(cellContent[queryStartOffset - 1] ?? '')) {
        queryStartOffset -= 1;
    }

    return queryStartOffset;
};

const isAtFormulaStart = (cellContent: string, queryStartOffset: number, cursorOffset: number): boolean =>
    queryStartOffset === 1 && cursorOffset === 1 && cellContent[0] === '=';

/*
 * Validates that the extracted query is a valid start to a formula function name.
 */
const FORMULA_QUERY_PATTERN = /^[A-Za-z_.]+$/;
const validateExtractedQuery = (cellContent: string, queryStartOffset: number, cursorOffset: number): boolean => {
    const query = cellContent.slice(queryStartOffset, cursorOffset);
    if (!query) return isAtFormulaStart(cellContent, queryStartOffset, cursorOffset);
    if (!FORMULA_QUERY_PATTERN.test(query)) return false;
    return true;
};

/*
 * Check if the character immediately before the query is a trigger character for formula suggestions.
 */
const FORMULA_QUERY_TRIGGER_CHARACTER_PATTERN = /[=(,+\-*/^&<>\s]/;
const isQueryAfterTriggerCharacter = (cellContent: string, queryStartOffset: number): boolean => {
    const triggerCharacter = cellContent[queryStartOffset - 1] ?? '';
    return FORMULA_QUERY_TRIGGER_CHARACTER_PATTERN.test(triggerCharacter);
};

/**
 * Returns the query bounds for a formula suggestion at the current caret position.
 * Gets the text between the current cursor position and the start of the formula token, if any.
 * Returns null if the cursor is not in a valid position for a formula suggestion.
 * @param cellContent The text content of the cell.
 * @param cursorOffset The current cursor position in the cell content.
 * @returns A FormulaPickerSession object with the query start and end offsets, or null if not valid.
 */
export const createFormulaPickerSessionAtCursor = (
    cellContent: string,
    cursorOffset: number,
): FormulaPickerSession | null => {
    if (!isFormula(cellContent) || cursorOffset < 1 || cursorOffset > cellContent.length) return null;

    const queryStartOffset = findQueryStartOffset(cellContent, cursorOffset);

    if (!validateExtractedQuery(cellContent, queryStartOffset, cursorOffset)) return null;

    if (!isQueryAfterTriggerCharacter(cellContent, queryStartOffset)) return null;

    return createFormulaPickerSession(cellContent, queryStartOffset);
};
